import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { 
  ArrowLeft, 
  Plus, 
  X, 
  User, 
  Briefcase, 
  Target,
  Check,
  Sparkles
} from 'lucide-react';
import { api } from '../services/api';

const NewPerson = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: '',
    team: '',
    location: '',
    availability: 'Available',
    experience: '',
    bio: '',
    skills: []
  });

  const [availableSkills, setAvailableSkills] = useState([]);

  useEffect(() => {
    let isMounted = true;
    api.getAllUniqueSkills().then(skills => {
      if (isMounted && Array.isArray(skills) && skills.length > 0) {
        setAvailableSkills(skills);
      }
    }).catch(() => {});
    return () => { isMounted = false; };
  }, []);

  const [newSkill, setNewSkill] = useState({
    name: '',
    level: 3,
    years: 1,
    validated: false
  });

  const skillLevels = [
    { value: 1, label: 'Beginner (1)' },
    { value: 2, label: 'Basic (2)' },
    { value: 3, label: 'Intermediate (3)' },
    { value: 4, label: 'Advanced (4)' },
    { value: 5, label: 'Expert (5)' }
  ];

  const availabilityOptions = ['Available', 'Partially Available', 'Not Available'];
  const teamOptions = ['Manufacturing', 'Quality', 'Digital', 'PMO', 'Engineering', 'Design', 'Product'];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const addSkill = () => {
    if (newSkill.name.trim()) {
      setFormData(prev => ({
        ...prev,
        skills: [...prev.skills, { ...newSkill, id: Date.now() }]
      }));
      setNewSkill({ name: '', level: 3, years: 1, validated: false });
    }
  };

  const removeSkill = (skillId) => {
    setFormData(prev => ({
      ...prev,
      skills: prev.skills.filter(skill => skill.id !== skillId)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prepare the person data for storage
    const personData = {
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      role: formData.role,
      team: formData.team,
      location: formData.location,
      availability: formData.availability,
      experience: formData.experience,
      bio: formData.bio,
      skills: formData.skills.map(skill => skill.name) // Convert skills to simple array for display
    };

    // Add new person with unique ID
    const newPerson = {
      ...personData,
      id: `p_${Date.now()}`,
      rating: 4.8,
      skills: formData.skills.map(skill => skill.name)
    };
    
    // Save to Neo4j database on server (and sync to cache)
    try {
      await api.createPerson(newPerson);
      console.log('Person persisted to server Neo4j database:', newPerson);
    } catch (err) {
      console.warn('Fallback: saved locally due to error:', err);
    }
    
    // Navigate back to people page
    navigate('/people');
  };

  const getSkillLevelColor = (level) => {
    if (level >= 4) return isDarkMode ? 'bg-green-500/20 text-green-400' : 'bg-emerald-50 text-emerald-800 border border-emerald-300 font-semibold';
    if (level >= 3) return isDarkMode ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-50 text-blue-800 border border-blue-300 font-semibold';
    if (level >= 2) return isDarkMode ? 'bg-amber-500/20 text-amber-300' : 'bg-amber-100 text-amber-950 border border-amber-300 font-bold';
    return isDarkMode ? 'bg-red-500/20 text-red-400' : 'bg-red-50 text-red-800 border border-red-300 font-semibold';
  };

  return (
    <div className={`max-w-4xl mx-auto space-y-8 transition-colors duration-200 ${
      isDarkMode ? 'text-white' : 'text-light-950'
    }`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/people')}
            className={`p-2 rounded-lg transition-all duration-200 ${
              isDarkMode 
                ? 'bg-dark-800 hover:bg-dark-700 text-white' 
                : 'bg-light-100 hover:bg-light-200 text-light-700 shadow-sm hover:shadow-md'
            }`}
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-3xl font-bold">Add New Team Member</h1>
            <p className={`transition-colors duration-200 ${
              isDarkMode ? 'text-dark-300' : 'text-light-600'
            }`}>
              Add a new person to your team with their skills and information
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Basic Information */}
        <div className={`card transition-all duration-200 ${
          isDarkMode ? 'dark' : 'light'
        }`}>
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-2 bg-blue-500/20 rounded-lg">
              <User className="h-6 w-6 text-blue-400" />
            </div>
            <h2 className="text-xl font-semibold">Basic Information</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className={`block text-sm font-medium mb-2 transition-colors duration-200 ${
                isDarkMode ? 'text-dark-300' : 'text-light-700'
              }`}>
                Full Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                className={`input-field w-full transition-all duration-200 ${
                  isDarkMode ? 'dark' : 'light'
                }`}
                placeholder="Enter full name"
              />
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 transition-colors duration-200 ${
                isDarkMode ? 'text-dark-300' : 'text-light-700'
              }`}>
                Email Address *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                className={`input-field w-full transition-all duration-200 ${
                  isDarkMode ? 'dark' : 'light'
                }`}
                placeholder="Enter email address"
              />
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 transition-colors duration-200 ${
                isDarkMode ? 'text-dark-300' : 'text-light-700'
              }`}>
                Phone Number
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className={`input-field w-full transition-all duration-200 ${
                  isDarkMode ? 'dark' : 'light'
                }`}
                placeholder="Enter phone number"
              />
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 transition-colors duration-200 ${
                isDarkMode ? 'text-dark-300' : 'text-light-700'
              }`}>
                Location
              </label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                className={`input-field w-full transition-all duration-200 ${
                  isDarkMode ? 'dark' : 'light'
                }`}
                placeholder="Enter location"
              />
            </div>
          </div>
        </div>

        {/* Role and Team Information */}
        <div className={`card transition-all duration-200 ${
          isDarkMode ? 'dark' : 'light'
        }`}>
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-2 bg-purple-500/20 rounded-lg">
              <Briefcase className="h-6 w-6 text-purple-400" />
            </div>
            <h2 className="text-xl font-semibold">Role & Team</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className={`block text-sm font-medium mb-2 transition-colors duration-200 ${
                isDarkMode ? 'text-dark-300' : 'text-light-700'
              }`}>
                Job Title *
              </label>
              <input
                type="text"
                name="role"
                value={formData.role}
                onChange={handleInputChange}
                required
                className={`input-field w-full transition-all duration-200 ${
                  isDarkMode ? 'dark' : 'light'
                }`}
                placeholder="e.g. Process Engineer, Quality Manager"
              />
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 transition-colors duration-200 ${
                isDarkMode ? 'text-dark-300' : 'text-light-700'
              }`}>
                Team *
              </label>
              <select
                name="team"
                value={formData.team}
                onChange={handleInputChange}
                required
                className={`input-field w-full transition-all duration-200 ${
                  isDarkMode ? 'dark' : 'light'
                }`}
              >
                <option value="">Select a team</option>
                {teamOptions.map(team => (
                  <option key={team} value={team}>{team}</option>
                ))}
              </select>
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 transition-colors duration-200 ${
                isDarkMode ? 'text-dark-300' : 'text-light-700'
              }`}>
                Availability
              </label>
              <select
                name="availability"
                value={formData.availability}
                onChange={handleInputChange}
                className={`input-field w-full transition-all duration-200 ${
                  isDarkMode ? 'dark' : 'light'
                }`}
              >
                {availabilityOptions.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 transition-colors duration-200 ${
                isDarkMode ? 'text-dark-300' : 'text-light-700'
              }`}>
                Years of Experience
              </label>
              <input
                type="text"
                name="experience"
                value={formData.experience}
                onChange={handleInputChange}
                className={`input-field w-full transition-all duration-200 ${
                  isDarkMode ? 'dark' : 'light'
                }`}
                placeholder="e.g. 5 years"
              />
            </div>
          </div>

          <div className="mt-6">
            <label className={`block text-sm font-medium mb-2 transition-colors duration-200 ${
              isDarkMode ? 'text-dark-300' : 'text-light-700'
            }`}>
              Bio / Description
            </label>
            <textarea
              name="bio"
              value={formData.bio}
              onChange={handleInputChange}
              rows="3"
              className={`input-field w-full transition-all duration-200 ${
                isDarkMode ? 'dark' : 'light'
              }`}
              placeholder="Brief description of background and expertise..."
            />
          </div>
        </div>

        {/* Skills */}
        <div className={`card transition-all duration-200 ${
          isDarkMode ? 'dark' : 'light'
        }`}>
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-2 bg-green-500/20 rounded-lg">
              <Target className="h-6 w-6 text-green-400" />
            </div>
            <h2 className="text-xl font-semibold">Skills</h2>
          </div>

          {/* Pre-loaded Skills Quick Add */}
          {availableSkills.length > 0 && (
            <div className={`p-3.5 rounded-xl mb-4 border ${
              isDarkMode ? 'bg-dark-800/60 border-dark-700' : 'bg-light-100 border-light-200'
            }`}>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-semibold uppercase tracking-wider flex items-center space-x-1.5 text-primary-400">
                  <Sparkles className="h-3.5 w-3.5" />
                  <span>Pre-loaded Organization Skills (Click to Toggle)</span>
                </label>
                <span className="text-[11px] text-gray-400">
                  {formData.skills.length} added
                </span>
              </div>
              <div className="flex flex-wrap gap-1.5 max-h-36 overflow-y-auto pr-1">
                {availableSkills.map((sk) => {
                  const isAdded = formData.skills.some(
                    s => s.name.toLowerCase() === sk.name.toLowerCase()
                  );
                  return (
                    <button
                      key={sk.name}
                      type="button"
                      onClick={() => {
                        if (isAdded) {
                          setFormData(prev => ({
                            ...prev,
                            skills: prev.skills.filter(s => s.name.toLowerCase() !== sk.name.toLowerCase())
                          }));
                        } else {
                          setFormData(prev => ({
                            ...prev,
                            skills: [...prev.skills, { id: Date.now() + Math.random(), name: sk.name, level: 3, years: 2, validated: true }]
                          }));
                        }
                      }}
                      className={`px-2.5 py-1 rounded-full text-xs font-medium border flex items-center space-x-1.5 transition-all ${
                        isAdded
                          ? 'bg-primary-600 text-white border-primary-500 shadow-sm font-semibold'
                          : isDarkMode
                            ? 'bg-dark-800 text-dark-300 border-dark-700 hover:border-primary-500 hover:text-white'
                            : 'bg-white text-light-700 border-light-300 hover:border-primary-500 hover:text-primary-600'
                      }`}
                    >
                      {isAdded ? <Check className="h-3 w-3" /> : <Plus className="h-3 w-3" />}
                      <span>{sk.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Add New Skill */}
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="md:col-span-2">
                <input
                  type="text"
                  list="new-person-skills-list"
                  value={newSkill.name}
                  onChange={(e) => setNewSkill(prev => ({ ...prev, name: e.target.value }))}
                  className={`input-field w-full transition-all duration-200 ${
                    isDarkMode ? 'dark' : 'light'
                  }`}
                  placeholder="Select pre-loaded skill or type name..."
                />
                <datalist id="new-person-skills-list">
                  {availableSkills.map((sk) => (
                    <option key={sk.name} value={sk.name}>
                      {sk.category ? `(${sk.category})` : ''}
                    </option>
                  ))}
                </datalist>
              </div>
              <div>
                <select
                  value={newSkill.level}
                  onChange={(e) => setNewSkill(prev => ({ ...prev, level: parseInt(e.target.value) }))}
                  className={`input-field w-full transition-all duration-200 ${
                    isDarkMode ? 'dark' : 'light'
                  }`}
                >
                  {skillLevels.map(level => (
                    <option key={level.value} value={level.value}>{level.label}</option>
                  ))}
                </select>
              </div>
              <div className="flex space-x-2">
                <input
                  type="number"
                  min="0"
                  max="20"
                  value={newSkill.years}
                  onChange={(e) => setNewSkill(prev => ({ ...prev, years: parseInt(e.target.value) || 0 }))}
                  className={`input-field flex-1 transition-all duration-200 ${
                    isDarkMode ? 'dark' : 'light'
                  }`}
                  placeholder="Years"
                />
                <button
                  type="button"
                  onClick={addSkill}
                  className="btn-primary px-4"
                >
                  <Plus className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="validated"
                checked={newSkill.validated}
                onChange={(e) => setNewSkill(prev => ({ ...prev, validated: e.target.checked }))}
                className="rounded border-gray-300 text-primary-500 focus:ring-primary-500"
              />
              <label htmlFor="validated" className={`text-sm transition-colors duration-200 ${
                isDarkMode ? 'text-dark-300' : 'text-light-700'
              }`}>
                Skill is validated/certified
              </label>
            </div>
          </div>

          {/* Skills List */}
          {formData.skills.length > 0 && (
            <div className="mt-6">
              <h3 className={`text-lg font-medium mb-4 transition-colors duration-200 ${
                isDarkMode ? 'text-dark-200' : 'text-light-800'
              }`}>
                Added Skills
              </h3>
              <div className="space-y-3">
                {formData.skills.map((skill) => (
                  <div key={skill.id} className={`flex items-center justify-between p-4 rounded-lg transition-colors duration-200 ${
                    isDarkMode ? 'bg-dark-800' : 'bg-light-100'
                  }`}>
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-2">
                        <span className="font-medium">{skill.name}</span>
                        {skill.validated && (
                          <span className="text-green-400 text-sm">✓</span>
                        )}
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSkillLevelColor(skill.level)}`}>
                        Level {skill.level}
                      </span>
                      <span className={`text-sm transition-colors duration-200 ${
                        isDarkMode ? 'text-dark-300' : 'text-light-600'
                      }`}>
                        {skill.years} {skill.years === 1 ? 'year' : 'years'}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeSkill(skill.id)}
                      className={`p-2 rounded-lg transition-all duration-200 ${
                        isDarkMode 
                          ? 'bg-dark-700 hover:bg-red-500/20 text-dark-300 hover:text-red-400' 
                          : 'bg-light-200 hover:bg-red-100 text-light-600 hover:text-red-600'
                      }`}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Form Actions */}
        <div className="flex items-center justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/people')}
            className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
              isDarkMode 
                ? 'bg-dark-800 hover:bg-dark-700 text-white border border-dark-600' 
                : 'bg-light-100 hover:bg-light-200 text-light-700 border border-light-300 shadow-sm hover:shadow-md'
            }`}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary px-8"
          >
            Add Team Member
          </button>
        </div>
      </form>
    </div>
  );
};

export default NewPerson;
